/**
 * Raise the browser-flag banners on demand, for a design review.
 *
 * Most global banners are raised by something true about the account, so they
 * can be set up from the database and simply appear. Two are not: "Account
 * recovered" and "Channels reduced" are one-off confirmations of something the
 * visitor just did, so they live in the browser rather than in the account.
 * There is no way to seed them from our side, and the review happens on a
 * phone, where opening a developer console is not a real option.
 *
 * So: `?banners=demo` sets both flags and reloads. The flags are the genuine
 * ones the banners already read — nothing here is a mock, and dismissing them
 * behaves exactly as it does after the real action.
 *
 * Never on the live site. The check is on the hostname rather than on a build
 * flag because dev and production are the same build, so a build flag would
 * say "production" in both places and gate nothing.
 */
const REVIEWABLE_HOSTS = /(^|\.)dev\.audiosanctum\.com$|^localhost$|^127\.0\.0\.1$/;

const FLAGS = [
  "account-just-recovered-at",
  "downgrade-channels-confirmed-at",
] as const;

export const applyDemoBanners = (): void => {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("banners");
  if (!mode) return;
  if (!REVIEWABLE_HOSTS.test(window.location.hostname)) return;

  try {
    if (mode === "demo") {
      // A fresh timestamp on every visit. The dismissal is remembered against
      // the timestamp, so reusing an old one would raise a banner the reviewer
      // had already dismissed and then hide it again on the next look.
      const now = new Date().toISOString();
      FLAGS.forEach((key) => localStorage.setItem(key, now));
    } else if (mode === "off") {
      FLAGS.forEach((key) => localStorage.removeItem(key));
    }
  } catch {
    /* private browsing with storage denied — nothing to show, nothing to fix */
  }

  // Strip the parameter so a refresh, a bookmark or the back button does not
  // silently re-raise a banner the reviewer has just dismissed.
  const url = new URL(window.location.href);
  url.searchParams.delete("banners");
  window.history.replaceState({}, "", url.toString());
};
